import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient.js";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import DashboardSidebarAdmin from "../components/Dashboard/DashboardSidebarAdmin.jsx";
import DashboardNavbarAdmin from "../components/Dashboard/DashboardNavbarAdmin.jsx";

// Helper for dynamic icon
const DynamicIcon = ({ name, className }) => {
    const IconComponent = Icons[name] || Icons.Box;
    return <IconComponent className={className} />;
};

const AdminFeaturesPage = () => {
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingFeature, setEditingFeature] = useState(null); // null = list mode, {} = create mode, {id...} = edit mode

    // Form State
    const [formData, setFormData] = useState({
        slug: "",
        name: "",
        description: "",
        icon: "Box",
        output_type: "text",
        webhook_route: "",
        is_active: true,
        sort_order: 0,
        status_badge: "" // New field for badge
    });

    // Custom badge state
    const [badgeType, setBadgeType] = useState("none"); // "none", "new", "populer", "custom"
    const [customBadgeText, setCustomBadgeText] = useState("");

    // Icon Upload State
    const [uploading, setUploading] = useState(false);

    const fetchFeatures = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from("app_features")
            .select("*")
            .order("sort_order", { ascending: true });

        if (error) console.error("Error fetching features", error);
        setFeatures(data || []);
        setLoading(false);
    };

    useEffect(() => {
        fetchFeatures();
    }, []);

    const handleEdit = (feature) => {
        setEditingFeature(feature);

        // Determine badge type
        const badge = feature.status_badge || "";
        let type = "none";
        let customText = "";

        if (badge === "New") type = "new";
        else if (badge === "Populer") type = "populer";
        else if (badge && badge.trim() !== "") {
            type = "custom";
            customText = badge;
        }

        setBadgeType(type);
        setCustomBadgeText(customText);

        setFormData({
            slug: feature.slug,
            name: feature.name,
            description: feature.description || "",
            icon: feature.icon || "",
            output_type: feature.output_type || "text",
            webhook_route: feature.webhook_route || "",
            is_active: feature.is_active,
            sort_order: feature.sort_order,
            code: feature.code || feature.slug, // Fallback to slug if code missing
            status_badge: feature.status_badge || ""
        });
    };

    const handleCreate = () => {
        setEditingFeature({});
        setBadgeType("none");
        setCustomBadgeText("");
        setFormData({
            slug: "",
            name: "",
            description: "",
            icon: "",
            output_type: "text",
            webhook_route: "",
            is_active: true,
            sort_order: features.length, // Default to next available index
            code: "", // Ensure code is initialized
            status_badge: ""
        });
    };

    const handleCancel = () => {
        setEditingFeature(null);
    };

    const handleFileUpload = async (e) => {
        try {
            const file = e.target.files[0];
            if (!file) return;

            setUploading(true);
            const fileExt = file.name.split('.').pop();
            const fileName = `${Date.now()}.${fileExt}`;
            const filePath = `feature-icons/${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('assets')
                .upload(filePath, file);

            if (uploadError) throw uploadError;

            // Update formData with the new path
            setFormData(prev => ({ ...prev, icon: filePath }));
        } catch (error) {
            alert('Error uploading image: ' + error.message);
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Prepare badge value based on type
            let badgeValue = null;
            if (badgeType === "new") badgeValue = "New";
            else if (badgeType === "populer") badgeValue = "Populer";
            else if (badgeType === "custom" && customBadgeText.trim()) badgeValue = customBadgeText.trim();

            const dataToSave = {
                ...formData,
                status_badge: badgeValue
            };

            if (editingFeature.id) {
                // Update
                const { error } = await supabase
                    .from("app_features")
                    .update(dataToSave)
                    .eq("id", editingFeature.id);
                if (error) throw error;
            } else {
                // Create
                const { error } = await supabase
                    .from("app_features")
                    .insert(dataToSave);
                if (error) throw error;
            }
            await fetchFeatures();
            setEditingFeature(null);
        } catch (err) {
            alert("Error saving feature: " + err.message);
        }
    };

    const handleMoveFeature = async (index, direction) => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === features.length - 1) return;

        const newFeatures = [...features];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        // Swap locally
        const temp = newFeatures[index];
        newFeatures[index] = newFeatures[targetIndex];
        newFeatures[targetIndex] = temp;

        // Update sort_order for all to match index
        newFeatures.forEach((f, idx) => f.sort_order = idx);

        setFeatures(newFeatures); // Optimistic update

        try {
            const updates = newFeatures.map(f =>
                supabase.from("app_features")
                    .update({ sort_order: f.sort_order })
                    .eq("id", f.id)
            );
            await Promise.all(updates);
        } catch (err) {
            console.error("Reorder error:", err);
            alert("Error saving order.");
            fetchFeatures(); // Revert
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure? This effectively removes the page.")) return;
        const { error } = await supabase.from("app_features").delete().eq("id", id);
        if (error) alert("Error deleting: " + error.message);
        else fetchFeatures();
    };

    return (
        <div className="h-screen overflow-hidden bg-slate-50 font-sans flex">
            <DashboardSidebarAdmin />
            <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
                <DashboardNavbarAdmin displayName="Admin" />
                <main className="flex-1 p-6 lg:p-10 overflow-y-auto relative scrollbar-thin">
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-2xl font-bold text-slate-800">Tool Management</h1>
                        <button
                            onClick={handleCreate}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium shadow-sm transition-colors"
                        >
                            + Add New Tool
                        </button>
                    </div>

                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                        <table className="w-full text-left">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="px-6 py-3 font-semibold text-slate-700 text-xs uppercase tracking-wider w-16 text-center">Order</th>
                                    <th className="px-6 py-3 font-semibold text-slate-700 text-xs uppercase tracking-wider">Icon</th>
                                    <th className="px-6 py-3 font-semibold text-slate-700 text-xs uppercase tracking-wider">Name</th>
                                    <th className="px-6 py-3 font-semibold text-slate-700 text-xs uppercase tracking-wider">Slug</th>
                                    <th className="px-6 py-3 font-semibold text-slate-700 text-xs uppercase tracking-wider">Type</th>
                                    <th className="px-6 py-3 font-semibold text-slate-700 text-xs uppercase tracking-wider">Badge</th>
                                    <th className="px-6 py-3 font-semibold text-slate-700 text-xs uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 font-semibold text-slate-700 text-xs uppercase tracking-wider text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {features.map((f, idx) => (
                                    <tr key={f.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-3">
                                            <div className="flex flex-col gap-1 items-center">
                                                <button
                                                    disabled={idx === 0}
                                                    onClick={() => handleMoveFeature(idx, 'up')}
                                                    className="text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:hover:text-slate-400 p-0.5"
                                                >
                                                    <Icons.ChevronUp size={14} />
                                                </button>
                                                <button
                                                    disabled={idx === features.length - 1}
                                                    onClick={() => handleMoveFeature(idx, 'down')}
                                                    className="text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:hover:text-slate-400 p-0.5"
                                                >
                                                    <Icons.ChevronDown size={14} />
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-6 py-3">
                                            {f.icon && (f.icon.includes('.') || f.icon.includes('/')) ? (
                                                <img
                                                    src={supabase.storage.from('assets').getPublicUrl(f.icon).data.publicUrl}
                                                    alt="icon"
                                                    className="w-10 h-10 object-contain"
                                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                />
                                            ) : (
                                                <div className="w-8 h-8 rounded bg-blue-50 text-blue-600 flex items-center justify-center overflow-hidden border border-blue-100">
                                                    <DynamicIcon name={f.icon} className="w-5 h-5" />
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-3 font-medium text-slate-900">{f.name}</td>
                                        <td className="px-6 py-3 text-slate-500 font-mono text-xs">{f.slug}</td>
                                        <td className="px-6 py-3">
                                            <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-[10px] uppercase font-bold tracking-wide border border-slate-200">{f.output_type}</span>
                                        </td>
                                        <td className="px-6 py-3">
                                            {f.status_badge ? (
                                                <span className={`px-2 py-1 rounded text-[10px] uppercase font-bold tracking-wide border ${f.status_badge === "New"
                                                    ? "bg-green-50 text-green-700 border-green-200"
                                                    : f.status_badge === "Populer"
                                                        ? "bg-orange-50 text-orange-700 border-orange-200"
                                                        : "bg-blue-50 text-blue-700 border-blue-200"
                                                    }`}>
                                                    {f.status_badge}
                                                </span>
                                            ) : (
                                                <span className="text-slate-300 text-xs">-</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-3">
                                            {f.is_active ? (
                                                <span className="text-green-700 font-bold text-[10px] bg-green-50 px-2 py-1 rounded-full border border-green-200">Active</span>
                                            ) : (
                                                <span className="text-slate-500 font-bold text-[10px] bg-slate-100 px-2 py-1 rounded-full border border-slate-200">Draft</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-3">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleEdit(f)}
                                                    className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    title="Edit Tool"
                                                >
                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(f.id)}
                                                    className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                                                    title="Delete Tool"
                                                >
                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {features.length === 0 && !loading && (
                                    <tr><td colSpan="8" className="p-10 text-center text-slate-400">No features found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* MODAL OVERLAY */}
                    {editingFeature && (
                        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                            {/* Backdrop */}
                            <div
                                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
                                onClick={handleCancel}
                            />

                            {/* Modal Content */}
                            <div className="relative bg-white rounded-xl border border-slate-200 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto flex flex-col animate-fade-in-up">
                                <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                                    <h2 className="text-lg font-bold text-slate-800">
                                        {editingFeature.id ? "Edit Tool" : "Create New Tool"}
                                    </h2>
                                    <button
                                        onClick={handleCancel}
                                        className="text-slate-400 hover:text-slate-600 transition-colors p-1 rounded hover:bg-slate-200"
                                    >
                                        <Icons.X className="w-5 h-5" />
                                    </button>
                                </div>

                                <div className="p-6 overflow-y-auto">
                                    <form onSubmit={handleSubmit} className="space-y-5">
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-1">Name</label>
                                            <input
                                                type="text"
                                                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                                value={formData.name}
                                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                                placeholder="e.g. AI Text Generator"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-1">Slug (URL Path)</label>
                                            <input
                                                type="text"
                                                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all font-mono"
                                                value={formData.slug}
                                                onChange={e => {
                                                    const lowercaseSlug = e.target.value.toLowerCase();
                                                    setFormData({
                                                        ...formData,
                                                        slug: lowercaseSlug,
                                                        code: lowercaseSlug,
                                                        webhook_route: lowercaseSlug // Auto-sync webhook_route
                                                    });
                                                }}
                                                placeholder="e.g. text-generator"
                                                required
                                            />
                                            <p className="text-[10px] text-slate-400 mt-1">This will be the URL: /dashboard/feature/<b>{formData.slug || '...'}</b> (auto lowercase)</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-1">Description</label>
                                            <textarea
                                                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                                value={formData.description}
                                                onChange={e => setFormData({ ...formData, description: e.target.value })}
                                                rows={3}
                                                placeholder="Briefly describe what this feature does..."
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* Left Col: Icon */}
                                            <div>
                                                <label className="block text-sm font-semibold text-slate-700 mb-2">Icon (Image Upload Only)</label>
                                                <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                                                    <div>
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            className="w-full text-xs text-slate-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                                                            onChange={handleFileUpload}
                                                            disabled={uploading}
                                                        />
                                                        {uploading && <p className="text-[10px] text-blue-500 mt-1 font-semibold animate-pulse">Uploading...</p>}

                                                        <div className="mt-3 flex items-center justify-center h-20 bg-white rounded border border-slate-200 overflow-hidden relative group">
                                                            {formData.icon && (formData.icon.includes('/') || formData.icon.includes('.')) ? (
                                                                <img
                                                                    src={supabase.storage.from('assets').getPublicUrl(formData.icon).data.publicUrl}
                                                                    alt="Preview"
                                                                    className="h-full w-auto object-contain transition-transform group-hover:scale-110"
                                                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                                />
                                                            ) : (
                                                                <div className="text-center p-2">
                                                                    {formData.icon ? <p className="text-[10px] text-slate-500 truncate max-w-[150px]">{formData.icon}</p> : <span className="text-[10px] text-slate-400">No Image</span>}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <p className="text-[9px] text-slate-400 mt-1 break-all truncate">{formData.icon}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Right Col: Type & Status */}
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Status Badge</label>
                                                    <select
                                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                                                        value={badgeType}
                                                        onChange={e => {
                                                            setBadgeType(e.target.value);
                                                            if (e.target.value !== "custom") setCustomBadgeText("");
                                                        }}
                                                    >
                                                        <option value="none">None (No Badge)</option>
                                                        <option value="new">New</option>
                                                        <option value="populer">Populer</option>
                                                        <option value="custom">Custom Badge</option>
                                                    </select>
                                                    <p className="text-[10px] text-slate-400 mt-1">Display a badge next to feature name</p>
                                                </div>

                                                {badgeType === "custom" && (
                                                    <div>
                                                        <label className="block text-sm font-semibold text-slate-700 mb-1">Custom Badge Text</label>
                                                        <input
                                                            type="text"
                                                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                                            value={customBadgeText}
                                                            onChange={e => setCustomBadgeText(e.target.value)}
                                                            placeholder="e.g. Beta, Coming Soon"
                                                            maxLength={20}
                                                        />
                                                        <p className="text-[10px] text-slate-400 mt-1">Max 20 characters</p>
                                                    </div>
                                                )}

                                                <div>
                                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Output Type</label>
                                                    <select
                                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                                                        value={formData.output_type}
                                                        onChange={e => setFormData({ ...formData, output_type: e.target.value })}
                                                    >
                                                        <option value="text">Text (JSON / Markdown / HTML)</option>
                                                        <option value="writer">Text Writer (Context Supported)</option>
                                                        <option value="chatbox">Chat Application (Full Page Chat)</option>
                                                        <option value="image">Image (Binary File)</option>
                                                        <option value="audio">Audio (Binary File)</option>
                                                        <option value="video">Video (Binary File)</option>
                                                        <option value="file">Document / File (Binary)</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Default Webhook Route (Auto)</label>
                                                    <input
                                                        type="text"
                                                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono text-slate-500 bg-slate-50 cursor-not-allowed"
                                                        value={formData.webhook_route || ""}
                                                        readOnly
                                                        placeholder="Auto-generated from slug"
                                                    />
                                                    <p className="text-[10px] text-slate-400 mt-1">⚡Otomatis.</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Sort Order</label>
                                                    <input
                                                        type="number"
                                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                                        value={formData.sort_order}
                                                        onChange={e => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                                                    />
                                                </div>
                                                <div className="flex items-center pt-2">
                                                    <label className="flex items-center gap-2 cursor-pointer select-none bg-slate-50 px-3 py-2 rounded-lg border border-slate-200 w-full hover:bg-slate-100 transition-colors">
                                                        <input
                                                            type="checkbox"
                                                            checked={formData.is_active}
                                                            onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                                                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                                                        />
                                                        <span className="text-sm font-medium text-slate-700">Active Feature</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex gap-3 pt-6 border-t border-slate-100 mt-4">
                                            <button
                                                type="button"
                                                onClick={handleCancel}
                                                className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-medium hover:bg-slate-50 transition-colors text-sm"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={uploading}
                                                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                            >
                                                {editingFeature.id ? "Save Changes" : "Create Tool"}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default AdminFeaturesPage;
